import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  ButtonGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Divider,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Switch,
  FormControlLabel,
  LinearProgress,
  Tooltip,
  Alert,
  Snackbar,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  Add as AddIcon,
  Close as CloseIcon,
  Dashboard as DashboardIcon,
  School as SchoolIcon,
  Assignment as AssignmentIcon,
  Assessment as AssessmentIcon,
  Visibility as VisibilityIcon,
  Share as ShareIcon,
  Logout as LogoutIcon,
  Person as PersonIcon,
  Error as ErrorIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  AccessTime as AccessTimeIcon,
  Event as EventIcon,
  Settings as SettingsIcon,
  Menu as MenuIcon,
  Warning as WarningIcon,
  Cancel as CancelIcon,
  ExpandMore as ExpandMoreIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { API_ENDPOINTS } from '../config/api';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import ListItemButton from '@mui/material/ListItemButton';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import CssBaseline from '@mui/material/CssBaseline';
import Drawer from '@mui/material/Drawer';
import Checkbox from '@mui/material/Checkbox';

// All icons are already imported from @mui/icons-material above

// QuestionAccordion component for collapsible question display
const QuestionAccordion = ({ question, index, studentAnswer, correctAnswer, isCorrect }) => {
  const [expanded, setExpanded] = useState(false);

  const handleChange = (event, isExpanded) => {
    setExpanded(isExpanded);
  };

  return (
    <Accordion
      expanded={expanded}
      onChange={handleChange}
      sx={{
        mb: 2,
        border: '1px solid #e0e0e0',
        borderLeft: `4px solid ${isCorrect ? '#4caf50' : '#f44336'}`,
        '&:before': {
          display: 'none',
        },
        boxShadow: 'none'
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          bgcolor: 'white',
          '&:hover': {
            bgcolor: '#f5f5f5'
          }
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', mr: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            Question {index + 1}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {isCorrect ? (
              <Box sx={{ display: 'flex', alignItems: 'center', color: '#4caf50' }}>
                <CheckCircleIcon sx={{ mr: 1, fontSize: '1.2rem' }} />
                <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                  Correct
                </Typography>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', color: '#f44336' }}>
                <CancelIcon sx={{ mr: 1, fontSize: '1.2rem' }} />
                <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                  {studentAnswer.trim() === '' ? 'Not Answered' : 'Incorrect'}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ bgcolor: 'white', pt: 0 }}>
        {/* Question Text */}
        <Typography variant="body1" sx={{ mb: 2, fontWeight: 'medium' }}>
          {question.questionText || question.question_text || 'Question text not available'}
        </Typography>

        {/* Options (if available) */}
        {question.options && question.options.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
              Options:
            </Typography>
            {question.options.map((option, optionIndex) => (
              <Typography
                key={optionIndex}
                variant="body2"
                sx={{
                  ml: 2,
                  mb: 0.5,
                  color: option === correctAnswer ? '#4caf50' : 'text.primary',
                  fontWeight: option === correctAnswer ? 'bold' : 'normal'
                }}
              >
                {String.fromCharCode(65 + optionIndex)}. {option}
                {option === correctAnswer && ' ✓'}
              </Typography>
            ))}
          </Box>
        )}

        {/* Student Answer */}
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
              Student Answer:
            </Typography>
            <Typography
              variant="body2"
              sx={{
                p: 1,
                bgcolor: studentAnswer.trim() === '' ? '#f5f5f5' : (isCorrect ? '#e8f5e8' : '#ffeaea'),
                borderRadius: 1,
                border: '1px solid #e0e0e0',
                fontStyle: studentAnswer.trim() === '' ? 'italic' : 'normal'
              }}
            >
              {studentAnswer.trim() === '' ? 'No answer provided' : studentAnswer}
            </Typography>
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
              Correct Answer:
            </Typography>
            <Typography
              variant="body2"
              sx={{
                p: 1,
                bgcolor: '#e8f5e8',
                borderRadius: 1,
                border: '1px solid #4caf50',
                fontWeight: 'medium'
              }}
            >
              {correctAnswer}
            </Typography>
          </Box>
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

// Add this helper function at the top-level, after imports but before the TeacherDashboard function
const getExamStatus = (exam) => {
  const now = new Date();
  const examDate = new Date(exam.date);
  const examEndTime = new Date(examDate.getTime() + (exam.duration * 60000));

  if (now < examDate) {
    return 'upcoming';
  } else if (now >= examDate && now <= examEndTime) {
    return 'ongoing';
  } else {
    return 'completed';
  }
};

function TeacherDashboard() {
  const [user, setUser] = useState(null);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [students, setStudents] = useState([]);
  const [exams, setExams] = useState([]);
  const [results, setResults] = useState([]);
  const [proctoringLogs, setProctoringLogs] = useState([]);
  const [viewLogsDialogOpen, setViewLogsDialogOpen] = useState(false);
  const [selectedLogId, setSelectedLogId] = useState(null);
  const [selectedStudentId, setSelectedStudentId] = useState(null);
  const [selectedExamId, setSelectedExamId] = useState(null);
  const [warningDialogOpen, setWarningDialogOpen] = useState(false);
  const [selectedWarning, setSelectedWarning] = useState(null);
  const [answersDialogOpen, setAnswersDialogOpen] = useState(false);
  const [selectedResult, setSelectedResult] = useState(null);
  const [dashboardStats, setDashboardStats] = useState({
    totalStudents: 0,
    totalExams: 0,
    completedExams: 0,
    upcomingExams: 0,
    ongoingExams: 0
  });
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [activeSection, setActiveSection] = useState('dashboard');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [examDialogOpen, setExamDialogOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  
  // Exam creation state
  const [examTitle, setExamTitle] = useState('');
  const [examDuration, setExamDuration] = useState(10);
  const [examDate, setExamDate] = useState('');
  const [examDescription, setExamDescription] = useState('');
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [examSubject, setExamSubject] = useState('datascience');
  const [availableSubjects, setAvailableSubjects] = useState({});
  
  const drawerWidth = 240;
  
  const navigate = useNavigate();

  const [selectedStudentByExam, setSelectedStudentByExam] = useState({});
  
  const [resultsStartDate, setResultsStartDate] = useState('');
  const [resultsEndDate, setResultsEndDate] = useState('');
  
  const [deleteStudentDialogOpen, setDeleteStudentDialogOpen] = useState(false);
  const [studentToDelete, setStudentToDelete] = useState(null);
  
  // 1. Add state for number of questions
  const [numQuestions, setNumQuestions] = useState(10);
  
  const [teacherSubjectFilter, setTeacherSubjectFilter] = useState('');
  const [teacherDateFilter, setTeacherDateFilter] = useState('');
  
  useEffect(() => {
    // Check if user is logged in and has teacher role
    const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
    
    if (!storedUser || !storedUser.username) {
      // Redirect to login if not logged in
      navigate('/');
      return;
    }
    
    if (storedUser.role !== 'teacher') {
      // Redirect to appropriate dashboard based on role
      if (storedUser.role === 'admin') {
        navigate('/admin-dashboard');
      } else if (storedUser.role === 'student') {
        navigate('/student-dashboard');
      } else {
        navigate('/');
      }
      return;
    }
    
    setUser(storedUser);
    
    // Fetch initial data
    fetchStudents(storedUser);
    fetchExams(storedUser);
    fetchResults(storedUser);
    fetchProctoringLogs(storedUser);
    fetchAvailableSubjects();
    
    // Initialize exam date to today
    const today = new Date();
    const formattedDate = today.toISOString().split('T')[0];
    setExamDate(formattedDate);
    
    // Set up interval to refresh data every 30 seconds like admin dashboard
    const intervalId = setInterval(() => {
      if (storedUser) {
        // Silent auto-refresh using same pattern as admin dashboard
        fetchStudents(storedUser);
        fetchExams(storedUser);
        fetchResults(storedUser);
        fetchProctoringLogs(storedUser);
      }
    }, 30000); // 30 seconds interval to match admin dashboard
    
    // Clean up interval on component unmount
    return () => clearInterval(intervalId);
  }, [navigate]);
  
  // Update dashboard stats whenever data changes
  useEffect(() => {
    const now = new Date().toISOString();
    
    // Calculate stats based on exam status
    const completedExams = exams.filter(exam => exam.status === 'completed').length;
    const ongoingExams = exams.filter(exam => exam.status === 'ongoing').length;
    const upcomingExams = exams.filter(exam => exam.status === 'upcoming').length;
    
    setDashboardStats({
      totalStudents: students.length,
      totalExams: exams.length,
      completedExams,
      ongoingExams,
      upcomingExams
    });
  }, [exams, students]);
  
  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };
  
  const handleSectionChange = (section) => {
    setActiveSection(section);
    setMobileOpen(false);
  };
  
  const handleOpenDialog = () => {
    // Reset form fields
    setUsername('');
    setPassword('');
    setName('');
    setEmail('');
    setError('');
    setDialogOpen(true);
  };
  
  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  // Email validation function
  const validateEmail = (email) => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email);
  };

  // Password validation function
  const validatePassword = (password) => {
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
    return passwordRegex.test(password);
  };
  
  const handleOpenExamDialog = () => {
    setExamTitle('');
    setExamDuration(10);
    setNumQuestions(10);
    setExamDate('');
    setExamDescription('');
    setSelectedStudents([]);
    setExamSubject('datascience');
    setExamDialogOpen(true);
  };
  
  const handleCloseExamDialog = () => {
    setExamTitle('');
    setExamDuration(10);
    setNumQuestions(10);
    setExamDescription('');
    setSelectedStudents([]);
    setExamSubject('datascience');
    setExamDialogOpen(false);
  };
  
  const fetchStudents = async (user) => {
    try {
      const response = await fetch(`${API_ENDPOINTS.TEACHER_STUDENTS}?teacher_username=${user.username}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${btoa(JSON.stringify(user))}`
        }
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage;
        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.error || 'Failed to fetch students';
        } catch (e) {
          // If the response is not valid JSON
          errorMessage = errorText || 'Failed to fetch students';
        }
        throw new Error(errorMessage);
      }
      
      const data = await response.json();
      setStudents(data.students || []);
    } catch (error) {
      console.error('Error fetching students:', error);
      setError('Failed to load students: ' + error.message);
      showSnackbar('Failed to load students: ' + error.message, 'error');
    }
  };
  
  const updateExamStatus = async (examId, newStatus, shareResults = false) => {
    try {
      setLoading(true);
      const user = JSON.parse(localStorage.getItem('user') || '{}');

      console.log('Updating exam status:', {
        exam_id: examId,
        status: newStatus,
        share_results: shareResults
      });

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/teacher/exams/update-status`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${btoa(JSON.stringify(user))}`
        },
        body: JSON.stringify({
          exam_id: examId,
          status: newStatus,
          share_results: shareResults
        })
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage;
        try {
          const errorJson = JSON.parse(errorText);
          errorMessage = errorJson.error || 'Failed to update exam status';
        } catch (e) {
          errorMessage = errorText || 'Failed to update exam status';
        }
        throw new Error(errorMessage);
      }
      
      const data = await response.json();
      
      // Show success message
      setSnackbarMessage(`Exam status updated to ${newStatus}${shareResults ? ' and results shared' : ''}`);
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      
      // Refresh data
      fetchExams(user);
      fetchResults(user);
    } catch (error) {
      console.error('Error updating exam status:', error);
      setSnackbarMessage(error.message || 'Failed to update exam status');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const fetchExams = async (user) => {
    try {
      const response = await fetch(API_ENDPOINTS.TEACHER_EXAMS, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${btoa(JSON.stringify(user))}`
        }
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage;
        try {
          const errorJson = JSON.parse(errorText);
          errorMessage = errorJson.error || 'Failed to fetch exams';
        } catch (e) {
          errorMessage = errorText || 'Failed to fetch exams';
        }
        throw new Error(errorMessage);
      }
      
      const data = await response.json();
      setExams(data.exams || []);
    } catch (error) {
      console.error('Error fetching exams:', error);
      setError('Failed to load exams');
    }
  };

  const fetchResults = async (user) => {
    try {
      // Create a token by base64 encoding the user object
      const token = btoa(JSON.stringify(user));
      
      const response = await fetch(API_ENDPOINTS.TEACHER_RESULTS, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (response.ok) {
        setResults(data.results || []);
        console.log('Results data received:', data);
        console.log('Number of results:', data.results ? data.results.length : 0);
      } else {
        console.error('Error fetching results:', data.error);
        showSnackbar(data.message || data.error || 'Failed to fetch results', 'error');
      }
    } catch (err) {
      console.error('Error fetching results:', err);
      showSnackbar('Error fetching results: ' + err.message, 'error');
      setError('Failed to load results');
    }
  };

  const fetchProctoringLogs = async (user) => {
    try {
      // Create a token by base64 encoding the user object
      const token = btoa(JSON.stringify(user));

      const response = await fetch(API_ENDPOINTS.TEACHER_PROCTORING_LOGS, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (response.ok) {
        setProctoringLogs(data.logs || []);
      } else {
        console.error('Error fetching proctoring logs:', data.error);
        showSnackbar(data.message || data.error || 'Failed to fetch proctoring logs', 'error');
      }
    } catch (err) {
      console.error('Error fetching proctoring logs:', err);
      showSnackbar('Error fetching proctoring logs: ' + err.message, 'error');
    }
  };

  const fetchAvailableSubjects = async () => {
    try {
      const response = await fetch(API_ENDPOINTS.SUBJECTS, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (response.ok) {
        setAvailableSubjects(data.subjects || {});
        console.log('Available subjects:', data.subjects);
      } else {
        console.error('Error fetching subjects:', data.message);
        showSnackbar(data.message || 'Failed to fetch available subjects', 'error');
      }
    } catch (err) {
      console.error('Error fetching subjects:', err);
      showSnackbar('Error fetching subjects: ' + err.message, 'error');
    }
  };

  const handleViewLogs = (examId, studentId) => {
    setSelectedExamId(examId);
    setSelectedStudentId(studentId);
    setViewLogsDialogOpen(true);
  };

  const handleOpenWarningDialog = (warning) => {
    setSelectedWarning(warning);
    setWarningDialogOpen(true);
  };

  const handleViewStudentAnswers = (result) => {
    setSelectedResult(result);
    setAnswersDialogOpen(true);
  };

  // Function to get warning title based on event type and description
  const getWarningTitle = (eventType, description) => {
    switch (eventType) {
      case 'AUDIO':
      case 'SPEECH':
        return 'Noise Detected';
      case 'GAZE':
        return 'Suspicious Eye Movement';
      case 'PERSON':
        return description.includes('Multiple') ? 'Multiple Persons Detected' : 'Face Detection Issue';
      case 'PHONE':
        return 'Phone Usage Detected';
      case 'SPOOF':
        return 'Face Tampering Suspected';
      case 'FOCUS':
        return 'Browser Focus Lost';
      case 'MOUTH':
        return 'Talking Detected';
      case 'HEAD':
        return 'Suspicious Head Movement';
      case 'SESSION':
        return 'Proctoring Session';
      default:
        return 'Warning / Alert!';
    }
  };
  
  const handleCreateStudent = async (e) => {
    e.preventDefault();
    
    if (!username || !password) {
      showSnackbar('Username and password are required', 'error');
      return;
    }

    // Email validation
    if (email && !validateEmail(email)) {
      showSnackbar('Please enter a valid email address', 'error');
      return;
    }

    // Password validation
    if (!validatePassword(password)) {
      showSnackbar('Password must be at least 8 characters long and contain at least one uppercase letter, one lowercase letter, one digit, and one special character (@$!%*?&)', 'error');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/teacher/create-student`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${btoa(JSON.stringify(user))}`
        },
        body: JSON.stringify({
          username,
          password,
          name: name || '',
          email: email || '',
          teacher_username: user.username,
          teacher_password: user.password
        }),
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage;
        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.error || 'Failed to create student account';
        } catch (e) {
          // If the response is not valid JSON
          errorMessage = errorText || 'Failed to create student account';
        }
        throw new Error(errorMessage);
      }
      
      const data = await response.json();
      
      // Add the new student to the students list
      setStudents([...students, data.student]);
      
      // Reset form fields
      setUsername('');
      setPassword('');
      setName('');
      setEmail('');
      
      // Show success message
      showSnackbar('Student account created successfully', 'success');
      
      // Close the dialog
      handleCloseDialog();
      
      // Refresh students list
      fetchStudents(user);
    } catch (error) {
      console.error('Error creating student account:', error);
      setError('Failed to create student account: ' + error.message);
      showSnackbar('Failed to create student account: ' + error.message, 'error');
    } finally {
      setLoading(false);
    }
  };
  
  const handleCreateExam = async (e) => {
    e.preventDefault();
    
    if (!examTitle || !examDate || !numQuestions) {
      showSnackbar('Please fill all required fields', 'error');
      return;
    }
    
    if (numQuestions < 10 || numQuestions > 100) {
      showSnackbar('Number of questions must be between 10 and 100', 'error');
      return;
    }
    
    if (selectedStudents.length === 0) {
      showSnackbar('Please select at least one student for the exam', 'warning');
      return;
    }
    
    try {
      setLoading(true);
      // Create a token by base64 encoding the user object
      const token = btoa(JSON.stringify(user));
      
      // Log the selected students for debugging
      console.log('Selected students for exam:', selectedStudents);
      
      const examData = {
        title: examTitle,
        date: examDate,
        duration: parseInt(examDuration), // Use the actual duration value
        num_questions: numQuestions,
        description: examDescription,
        students: selectedStudents,
        subject: examSubject
      };
      
      console.log('Sending exam data to server:', examData);
      
      const response = await fetch(API_ENDPOINTS.TEACHER_EXAMS, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(examData)
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage;
        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.error || 'Failed to create exam';
        } catch (e) {
          // If the response is not valid JSON
          errorMessage = errorText || 'Failed to create exam';
        }
        throw new Error(errorMessage);
      }
      
      const data = await response.json();
      
      // Add the new exam to the exams list
      setExams([...exams, data.exam]);
      
      // Show success message
      showSnackbar('Exam created successfully', 'success');
      
      // Close the dialog
      handleCloseExamDialog();
    } catch (error) {
      console.error('Error creating exam:', error);
      showSnackbar('Failed to create exam: ' + error.message, 'error');
    } finally {
      setLoading(false);
    }
  };
  
  const showSnackbar = (message, severity) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleManualRefresh = async () => {
    if (!user) {
      showSnackbar('User not authenticated', 'error');
      return;
    }

    try {
      setLoading(true);
      setError(''); // Clear any existing errors

      console.log('Starting manual refresh...');

      // Fetch all data sequentially like admin dashboard for better reliability
      await fetchStudents(user);
      await fetchExams(user);
      await fetchResults(user);
      await fetchProctoringLogs(user);
      await fetchAvailableSubjects();

      console.log('Manual refresh completed successfully');
      showSnackbar('Dashboard refreshed successfully', 'success');

    } catch (error) {
      console.error('Error during manual refresh:', error);
      setError('Failed to refresh dashboard. Please try again.');
      showSnackbar('Failed to refresh dashboard. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('user');
    navigate('/');
  };

  const handleDeleteStudent = async () => {
    if (!studentToDelete) return;
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/teacher/delete-student`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: studentToDelete.username,
          teacher_username: user.username,
          teacher_password: user.password
        })
      });
      const data = await response.json();
      if (response.ok && data.success) {
        showSnackbar('Student deleted successfully', 'success');
        setDeleteStudentDialogOpen(false);
        setStudentToDelete(null);
        fetchStudents(user);
      } else {
        showSnackbar(data.error || 'Failed to delete student', 'error');
      }
    } catch (err) {
      showSnackbar('Error deleting student: ' + err.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return <Box sx={{ p: 3 }}>Loading...</Box>;
  }

  // Sidebar content
  const drawer = (
    <Box sx={{ bgcolor: '#f5f5f5', height: '100%' }}>
      <Box sx={{ 
        p: 2, 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center',
        borderBottom: '1px solid #e0e0e0'
      }}>
        <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#0277bd' }}>
          ViGiLANT
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Online Exam Management
        </Typography>
      </Box>
      
      <List component="nav" sx={{ pt: 1 }}>
        <ListItemButton 
          selected={activeSection === 'dashboard'}
          onClick={() => handleSectionChange('dashboard')}
        >
          <ListItemIcon>
            <DashboardIcon color={activeSection === 'dashboard' ? 'primary' : 'inherit'} />
          </ListItemIcon>
          <ListItemText primary="Dashboard" />
        </ListItemButton>
        
        <ListItemButton 
          selected={activeSection === 'exams'}
          onClick={() => handleSectionChange('exams')}
        >
          <ListItemIcon>
            <AssignmentIcon color={activeSection === 'exams' ? 'primary' : 'inherit'} />
          </ListItemIcon>
          <ListItemText primary="Exams" />
        </ListItemButton>
        
        <ListItemButton 
          selected={activeSection === 'students'}
          onClick={() => handleSectionChange('students')}
        >
          <ListItemIcon>
            <PersonIcon color={activeSection === 'students' ? 'primary' : 'inherit'} />
          </ListItemIcon>
          <ListItemText primary="Students" />
        </ListItemButton>
        
        <ListItemButton 
          selected={activeSection === 'results'}
          onClick={() => handleSectionChange('results')}
        >
          <ListItemIcon>
            <AssessmentIcon color={activeSection === 'results' ? 'primary' : 'inherit'} />
          </ListItemIcon>
          <ListItemText primary="Results" />
        </ListItemButton>
        
        <ListItemButton 
          selected={activeSection === 'proctoring'}
          onClick={() => handleSectionChange('proctoring')}
        >
          <ListItemIcon>
            <VisibilityIcon color={activeSection === 'proctoring' ? 'primary' : 'inherit'} />
          </ListItemIcon>
          <ListItemText primary="Proctoring" />
        </ListItemButton>
        
        <Divider sx={{ my: 2 }} />
        
        <ListItemButton onClick={handleLogout}>
          <ListItemIcon>
            <LogoutIcon />
          </ListItemIcon>
          <ListItemText primary="Logout" />
        </ListItemButton>
      </List>
    </Box>
  );
  
  return (
    <>
      <Box sx={{ display: 'flex', height: '100vh' }}>
        <CssBaseline />
        
        {/* App Bar */}
        <AppBar
          position="fixed"
          sx={{
            width: { sm: `calc(100% - ${drawerWidth}px)` },
            ml: { sm: `${drawerWidth}px` },
            bgcolor: '#0277bd',
            boxShadow: 2
          }}
        >
          <Toolbar>
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2, display: { sm: 'none' } }}
            >
              <MenuIcon />
            </IconButton>
            <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
              {activeSection === 'dashboard' && 'Dashboard'}
              {activeSection === 'exams' && 'Exam Management'}
              {activeSection === 'students' && 'Student Management'}
              {activeSection === 'results' && 'Results'}
              {activeSection === 'proctoring' && 'Proctoring'}
            </Typography>

            {/* Manual Refresh Button */}
            <Tooltip title="Refresh Dashboard">
              <IconButton
                color="inherit"
                onClick={handleManualRefresh}
                disabled={loading}
                sx={{ mr: 1 }}
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>

            {/* Logout Button */}
            <Tooltip title="Logout">
              <IconButton
                color="inherit"
                onClick={handleLogout}
              >
                <LogoutIcon />
              </IconButton>
            </Tooltip>
          </Toolbar>
        </AppBar>
        
        {/* Sidebar */}
        <Box
          component="nav"
          sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
        >
          {/* Mobile drawer */}
          <Drawer
            variant="temporary"
            open={mobileOpen}
            onClose={handleDrawerToggle}
            ModalProps={{ keepMounted: true }}
            sx={{
              display: { xs: 'block', sm: 'none' },
              '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
            }}
          >
            {drawer}
          </Drawer>
          
          {/* Desktop drawer */}
          <Drawer
            variant="permanent"
            sx={{
              display: { xs: 'none', sm: 'block' },
              '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
            }}
            open
          >
            {drawer}
          </Drawer>
        </Box>
        
        {/* Main content */}
        <Box
          component="main"
          sx={{ 
            flexGrow: 1, 
            p: 3, 
            width: { sm: `calc(100% - ${drawerWidth}px)` },
            mt: { xs: 7, sm: 8 },
            bgcolor: '#f9f9f9',
            height: '100%',
            overflow: 'auto',
            boxShadow: 0
          }}
        >
          {/* Error message if any */}
          {error && (
            <Alert 
              severity="error" 
              sx={{ mb: 3 }}
              icon={<ErrorIcon fontSize="inherit" />}
            >
              {error}
            </Alert>
          )}
          
          {/* Dashboard Section */}
          {activeSection === 'dashboard' && (
            <>
              {/* Exam Statistics Section */}
              <Paper elevation={2} sx={{ mb: 3, p: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">
                    Exam Stats
                  </Typography>
                  <Typography variant="subtitle2" color="primary">
                    Students: {dashboardStats.totalStudents}
                  </Typography>
                </Box>
                
                <Grid container spacing={2}>
                  {/* Total Exams */}
                  <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ bgcolor: '#2196f3', color: 'white', height: '100%' }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="subtitle1">Total Exams</Typography>
                          <DeleteIcon />
                        </Box>
                        <Typography variant="h3" sx={{ mt: 2, mb: 1 }}>{dashboardStats.totalExams}</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  
                  {/* Exams Completed */}
                  <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ bgcolor: '#4caf50', color: 'white', height: '100%' }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="subtitle1">Exams Completed</Typography>
                          <CheckCircleIcon />
                        </Box>
                        <Typography variant="h3" sx={{ mt: 2, mb: 1 }}>{dashboardStats.completedExams}</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  
                  {/* Ongoing Exams */}
                  <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ bgcolor: '#ff9800', color: 'white', height: '100%' }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="subtitle1">On-going Exams</Typography>
                          <AccessTimeIcon />
                        </Box>
                        <Typography variant="h3" sx={{ mt: 2, mb: 1 }}>{dashboardStats.ongoingExams}</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  
                  {/* Upcoming Exams */}
                  <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ bgcolor: '#f44336', color: 'white', height: '100%' }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="subtitle1">Upcoming Exams</Typography>
                          <EventIcon />
                        </Box>
                        <Typography variant="h3" sx={{ mt: 2, mb: 1 }}>{dashboardStats.upcomingExams}</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </Paper>
              
              {/* Quick Links and Upcoming Exams Section */}
              <Grid container spacing={3}>
                {/* Quick Links */}
                <Grid item xs={12} md={6}>
                  <Paper elevation={2} sx={{ p: 2 }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                      Quick Links
                    </Typography>
                    
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      <Button variant="outlined" color="primary" size="small" onClick={() => handleSectionChange('exams')}>
                        Manage Exams
                      </Button>
                      <Button variant="outlined" color="primary" size="small" onClick={() => handleSectionChange('students')}>
                        Manage Students
                      </Button>
                      <Button variant="outlined" color="primary" size="small" onClick={() => handleSectionChange('results')}>
                        View Results
                      </Button>
                      <Button variant="outlined" color="primary" size="small" onClick={() => handleSectionChange('proctoring')}>
                        Proctoring Reports
                      </Button>
                    </Box>
                  </Paper>
                </Grid>
                
                {/* Upcoming Exams */}
                <Grid item xs={12} md={6}>
                  <Paper elevation={2} sx={{ p: 2 }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                      Upcoming Exams
                    </Typography>
                    
                    {exams.filter(exam => exam.status === "upcoming").slice(0, 3).map(exam => (
                      <Box key={exam._id} sx={{ mb: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                          <Typography variant="subtitle1" fontWeight="bold">{exam.title}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {new Date(exam.date).toLocaleDateString()} {new Date(exam.date).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                          </Typography>
                        </Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Duration: {exam.duration} minutes</Typography>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Chip label={`${exam.students?.length || 0} Students`} size="small" color="success" variant="outlined" />
                          <Chip label="Created" size="small" color="info" variant="outlined" />
                          <Chip label={new Date(exam.created_at || '').toLocaleDateString()} size="small" color="primary" variant="outlined" />
                        </Box>
                      </Box>
                    ))}
                    {exams.filter(exam => exam.status === "upcoming").length === 0 && (
                      <Typography variant="body2" color="text.secondary">No upcoming exams scheduled.</Typography>
                    )}
                  </Paper>
                </Grid>
              </Grid>
            </>
          )}
          
          {/* Exams Section */}
          {activeSection === 'exams' && (
            <Paper elevation={2} sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box>
                  <Typography variant="h6" component="h2">
                    Exam Management
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Sorted by latest first
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography variant="subtitle1" color="primary">
                    Total Exams: {exams.length}
                  </Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={handleOpenExamDialog}
                  >
                    CREATE EXAM
                  </Button>
                </Box>
              </Box>
              {/* Filter UI for teachers */}
              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <TextField
                  select
                  label="Subject"
                  value={teacherSubjectFilter}
                  onChange={e => setTeacherSubjectFilter(e.target.value)}
                  size="small"
                  sx={{ minWidth: 150 }}
                >
                  <MenuItem value="">All</MenuItem>
                  {Array.from(new Set(exams.map(e => e.subject))).map(subject => (
                    <MenuItem key={subject} value={subject}>
                      {subject?.charAt(0).toUpperCase() + subject?.slice(1)}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  label="Date"
                  type="date"
                  value={teacherDateFilter}
                  onChange={e => setTeacherDateFilter(e.target.value)}
                  size="small"
                  InputLabelProps={{ shrink: true }}
                />
              </Box>
              {/* End Filter UI */}
              {exams.length > 0 ? (
                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Title</TableCell>
                        <TableCell>Subject</TableCell>
                        <TableCell>Date</TableCell>
                        <TableCell>Duration</TableCell>
                        <TableCell>Created By</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Students</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {/* Sort and filter exams */}
                      {[...exams]
                        .filter(exam =>
                          (!teacherSubjectFilter || exam.subject === teacherSubjectFilter) &&
                          (!teacherDateFilter || exam.date.startsWith(teacherDateFilter))
                        )
                        .sort((a, b) => {
                          const dateA = a.created_at ? new Date(a.created_at) :
                                       a.date ? new Date(a.date) : new Date(0);
                          const dateB = b.created_at ? new Date(b.created_at) :
                                       b.date ? new Date(b.date) : new Date(0);
                          return dateB - dateA;
                        })
                        .map(exam => (
                          <TableRow key={exam._id}>
                            <TableCell>{exam.title}</TableCell>
                            <TableCell>
                              <Chip
                                label={availableSubjects[exam.subject]?.name || exam.subject?.charAt(0).toUpperCase() + exam.subject?.slice(1) || 'Data Science'}
                                size="small"
                                color={
                                  exam.subject === 'tableau' ? 'primary' :
                                  exam.subject === 'sql' ? 'secondary' :
                                  'success'
                                }
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell>{new Date(exam.date).toLocaleString()}</TableCell>
                            <TableCell>{exam.duration}</TableCell>
                            <TableCell>{exam.created_by || user?.username || '-'}</TableCell>
                            <TableCell>
                              <Chip
                                label={getExamStatus(exam).charAt(0).toUpperCase() + getExamStatus(exam).slice(1)}
                                color={getExamStatus(exam) === "completed" ? "success" : getExamStatus(exam) === "ongoing" ? "warning" : "default"}
                                size="small"
                              />
                            </TableCell>
                            <TableCell>{exam.students ? exam.students.length : 0}</TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography variant="body1">No exams found.</Typography>
              )}
            </Paper>
          )}
          
          {/* Students Section */}
          {activeSection === 'students' && (
            <Paper elevation={2} sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" component="h2">
                  Student Management
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="outlined"
                    color="primary"
                    startIcon={<RefreshIcon />}
                    onClick={handleManualRefresh}
                    disabled={loading}
                    size="small"
                  >
                    {loading ? 'Refreshing...' : 'Refresh'}
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={handleOpenDialog}
                  >
                    ADD STUDENT
                  </Button>
                </Box>
              </Box>
              
              {students.length > 0 ? (
                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Username</TableCell>
                        <TableCell>Name</TableCell>
                        <TableCell>Email</TableCell>
                        <TableCell>Created By</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {students.map((student) => (
                        <TableRow key={student._id || student.username}>
                          <TableCell>{student.username}</TableCell>
                          <TableCell>{student.name || '-'}</TableCell>
                          <TableCell>{student.email || '-'}</TableCell>
                          <TableCell>{student.created_by || user?.username || '-'}</TableCell>
                          <TableCell>
                            <IconButton
                              color="error"
                              onClick={() => { setStudentToDelete(student); setDeleteStudentDialogOpen(true); }}
                              title="Delete Student"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography variant="body1" sx={{ mt: 2 }}>
                  No student accounts found. Create your first student account.
                </Typography>
              )}
              {/* Delete Student Confirmation Dialog */}
              <Dialog
                open={deleteStudentDialogOpen}
                onClose={() => setDeleteStudentDialogOpen(false)}
                maxWidth="xs"
                fullWidth
              >
                <DialogTitle>Delete Student</DialogTitle>
                <DialogContent>
                  <Typography>Are you sure you want to delete student <b>{studentToDelete?.username}</b>?</Typography>
                </DialogContent>
                <DialogActions>
                  <Button onClick={() => setDeleteStudentDialogOpen(false)} color="inherit">Cancel</Button>
                  <Button onClick={handleDeleteStudent} color="error" variant="contained" disabled={loading}>
                    {loading ? 'Deleting...' : 'Delete'}
                  </Button>
                </DialogActions>
              </Dialog>
            </Paper>
          )}
          
          {/* Results Section */}
          {activeSection === 'results' && (
            <Paper elevation={2} sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box>
                  <Typography variant="h6" component="h2">
                    Exam Results
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Sorted by latest submissions first
                  </Typography>
                </Box>
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<RefreshIcon />}
                  onClick={handleManualRefresh}
                  disabled={loading}
                  size="small"
                >
                  {loading ? 'Refreshing...' : 'Refresh'}
                </Button>
              </Box>
              {/* Date Filter */}
              <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                <TextField
                  label="Start Date"
                  type="date"
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  value={resultsStartDate || ''}
                  onChange={e => setResultsStartDate(e.target.value)}
                />
                <TextField
                  label="End Date"
                  type="date"
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  value={resultsEndDate || ''}
                  onChange={e => setResultsEndDate(e.target.value)}
                />
              </Box>
              {results.length > 0 ? (
                <Grid container spacing={3}>
                  {/* Group results by exam */}
                  {(() => {
                    // Group results by exam_id
                    const resultsByExam = {};
                    results.forEach(result => {
                      const examId = result.exam_id || result.examId;
                      if (!resultsByExam[examId]) resultsByExam[examId] = [];
                      resultsByExam[examId].push(result);
                    });
                    // Get all exam IDs in sorted order (by latest submission)
                    const sortedExamIds = Object.keys(resultsByExam).sort((a, b) => {
                      // Sort by latest submission in each group
                      const latestA = Math.max(...resultsByExam[a].map(r => new Date(r.submitted_at || r.submission_date || 0).getTime()));
                      const latestB = Math.max(...resultsByExam[b].map(r => new Date(r.submitted_at || r.submission_date || 0).getTime()));
                      return latestB - latestA;
                    });
                    // Filter by date
                    const filteredExamIds = sortedExamIds.filter(examId => {
                      const examResults = resultsByExam[examId];
                      // If no filter, show all
                      if (!resultsStartDate && !resultsEndDate) return true;
                      // Check if any submission for this exam is in range
                      return examResults.some(r => {
                        const date = new Date(r.submitted_at || r.submission_date || 0);
                        if (resultsStartDate && date < new Date(resultsStartDate)) return false;
                        if (resultsEndDate && date > new Date(resultsEndDate + 'T23:59:59')) return false;
                        return true;
                      });
                    });
                    return (
                      <>
                        {filteredExamIds.map((examId, idx) => {
                          const exam = exams.find(e => e._id === examId || String(e._id) === String(examId)) || {};
                          const examResults = resultsByExam[examId];
                          // Find the latest submission date for this exam
                          const latestResult = examResults.reduce((latest, r) => {
                            const date = new Date(r.submitted_at || r.submission_date || 0);
                            return (!latest || date > latest) ? date : latest;
                          }, null);
                          // Use exam.date if available, else latest submission
                          const examDate = exam.date ? new Date(exam.date) : latestResult;
                          const examDateStr = examDate && !isNaN(examDate.getTime()) ? examDate.toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' }) : 'N/A';
                          return (
                            <Grid item xs={12} md={6} key={examId}>
                              <Accordion sx={{ mb: 2 }}>
                                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ display: 'flex', alignItems: 'center' }}>
                                  <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 0 }}>{exam.title || examId}</Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{examDateStr}</Typography>
                                  </Box>
                                  <Chip label={`${examResults.length} submissions`} color="primary" sx={{ ml: 2 }} />
                                  {/* Share Results Button */}
                                  {exam.status === 'completed' && !exam.results_shared && (
                                    <Tooltip title="Share Results">
                                      <span>
                                        <IconButton
                                          color="success"
                                          size="small"
                                          sx={{ ml: 1 }}
                                          disabled={loading}
                                          onClick={e => {
                                            e.stopPropagation();
                                            updateExamStatus(exam._id, 'completed', true);
                                          }}
                                        >
                                          <ShareIcon />
                                        </IconButton>
                                      </span>
                                    </Tooltip>
                                  )}
                                  {exam.status === 'completed' && exam.results_shared && (
                                    <Chip label="Results Shared" color="success" size="small" sx={{ ml: 2 }} />
                                  )}
                                </AccordionSummary>
                                <AccordionDetails>
                                  <TableContainer component={Paper} variant="outlined">
                                    <Table size="small">
                                      <TableHead>
                                        <TableRow>
                                          <TableCell>Student</TableCell>
                                          <TableCell>Submitted</TableCell>
                                          <TableCell>Questions Attempted</TableCell>
                                          <TableCell>Score</TableCell>
                                          <TableCell>View Answers</TableCell>
                                        </TableRow>
                                      </TableHead>
                                      <TableBody>
                                        {examResults.sort((a, b) => {
                                          const dateA = new Date(a.submitted_at || a.submission_date || 0).getTime();
                                          const dateB = new Date(b.submitted_at || b.submission_date || 0).getTime();
                                          return dateB - dateA;
                                        }).map((result, idx) => {
                                          const student = students.find(s => s._id === result.user_id || s.username === result.student_username) || {};
                                          const totalQuestions = result.questions ? result.questions.length :
                                            result.questions_with_answers ? result.questions_with_answers.length :
                                            result.answers ? Object.keys(result.answers).length : 0;
                                          const questionsAttempted = result.answers ?
                                            Object.values(result.answers).filter(answer => answer && answer.toString().trim() !== '').length : 0;
                                          return (
                                            <TableRow key={result._id || idx}>
                                              <TableCell>{student.name || result.student_name || result.student_username || result.user_id || 'Unknown'}</TableCell>
                                              <TableCell>{(() => {
                                                const dateValue = result.submitted_at || result.submission_date;
                                                if (!dateValue) return 'N/A';
                                                try {
                                                  const date = new Date(dateValue);
                                                  if (isNaN(date.getTime())) return 'Invalid Date';
                                                  return date.toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' });
                                                } catch {
                                                  return 'Invalid Date';
                                                }
                                              })()}</TableCell>
                                              <TableCell>
                                                <Chip size="small" label={`${questionsAttempted}/${totalQuestions}`} color={questionsAttempted === totalQuestions ? "success" : "warning"} />
                                              </TableCell>
                                              <TableCell>
                                                <Chip size="small" label={`${result.score !== undefined ? Math.round(result.score) : 0}%`} color="primary" />
                                              </TableCell>
                                              <TableCell>
                                                <Button 
                                                  variant="outlined" 
                                                  color="primary" 
                                                  size="small"
                                                  onClick={() => handleViewStudentAnswers(result)}
                                                  startIcon={<AssessmentIcon />}
                                                >
                                                  View Answers
                                                </Button>
                                              </TableCell>
                                            </TableRow>
                                          );
                                        })}
                                      </TableBody>
                                    </Table>
                                  </TableContainer>
                                </AccordionDetails>
                              </Accordion>
                            </Grid>
                          );
                        })}
                      </>
                    );
                  })()}
                </Grid>
              ) : (
                <Box sx={{ p: 4, textAlign: 'center' }}>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    No results found. Results will appear here after students complete exams.
                  </Typography>
                  <AssessmentIcon color="disabled" sx={{ fontSize: 60, opacity: 0.5 }} />
                </Box>
              )}
              <Alert severity="info" sx={{ mt: 3 }}>
                Students can only view their results after you share them. To share results, complete the exam and click the "Share Results" button.
              </Alert>
            </Paper>
          )}
          
          {/* Proctoring Section */}
          {activeSection === 'proctoring' && (
            <Paper elevation={2} sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" component="h2">
                  Proctoring Logs
                </Typography>
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<RefreshIcon />}
                  onClick={handleManualRefresh}
                  disabled={loading}
                  size="small"
                >
                  {loading ? 'Refreshing...' : 'Refresh'}
                </Button>
              </Box>
              
              {proctoringLogs.length > 0 ? (
                <Box>
                  {/* Group logs by exam */}
                  {(() => {
                    // Create a map of exam IDs to logs
                    const examLogsMap = {};

                    // Filter out invalid logs and group by exam ID
                    proctoringLogs
                      .filter(log => {
                        // Filter out logs with invalid timestamps
                        if (!log.timestamp) return false;

                        try {
                          const date = new Date(log.timestamp);
                          // Check if date is valid and reasonable
                          if (isNaN(date.getTime())) return false;
                          if (date.getFullYear() < 2020 || date.getFullYear() > 2030) return false;
                        } catch (e) {
                          return false;
                        }

                        // Filter out logs with generic/invalid descriptions
                        const description = log.description || '';
                        const invalidDescriptions = [
                          'warning / alert!',
                          'warning/alert!',
                          'alert',
                          'warning',
                          ''
                        ];
                        if (invalidDescriptions.includes(description.toLowerCase().trim())) return false;

                        // Filter out logs with missing event types
                        if (!log.event_type || log.event_type.trim() === '') return false;

                        return true;
                      })
                      .forEach(log => {
                        if (!examLogsMap[log.exam_id]) {
                          examLogsMap[log.exam_id] = [];
                        }
                        examLogsMap[log.exam_id].push(log);
                      });


                    
                    // Sort logs within each exam by timestamp (newest first)
                    Object.keys(examLogsMap).forEach(examId => {
                      examLogsMap[examId].sort((a, b) => {
                        const dateA = a.timestamp ? new Date(a.timestamp) : new Date(0);
                        const dateB = b.timestamp ? new Date(b.timestamp) : new Date(0);
                        return dateB - dateA; // Sort descending (newest first)
                      });
                    });
                    
                    // Convert the map to an array of exam objects with their logs
                    const examLogs = Object.keys(examLogsMap).map(examId => {
                      const exam = exams.find(e => e._id === examId) || {};


                      // Find the most recent log timestamp for this exam
                      const mostRecentTimestamp = examLogsMap[examId].reduce((latest, log) => {
                        const logTime = log.timestamp ? new Date(log.timestamp) : new Date(0);
                        return logTime > latest ? logTime : latest;
                      }, new Date(0));

                      return {
                        examId,
                        examTitle: exam.title || examId,
                        logs: examLogsMap[examId],
                        mostRecentTimestamp
                      };
                    });

                    // Sort exam groups by most recent activity (newest first)
                    examLogs.sort((a, b) => b.mostRecentTimestamp - a.mostRecentTimestamp);


                    
                    // Return the JSX for each exam card
                    return examLogs.map((examLog, index) => {
                      // Group logs by student for this exam, filtering out SESSION and 'Proctoring Session' logs
                      const studentLogsMap = {};
                      examLog.logs.forEach(log => {
                        // Filter out SESSION events and 'Proctoring Session' descriptions
                        if (
                          log.event_type === 'SESSION' ||
                          (typeof log.description === 'string' && log.description.toLowerCase().includes('proctoring session'))
                        ) {
                          return;
                        }
                        if (!studentLogsMap[log.user_id]) studentLogsMap[log.user_id] = [];
                        studentLogsMap[log.user_id].push(log);
                      });
                      // Get student IDs sorted by name (optional)
                      const studentIds = Object.keys(studentLogsMap).sort((a, b) => {
                        const nameA = (students.find(s => s._id === a)?.name || a).toLowerCase();
                        const nameB = (students.find(s => s._id === b)?.name || b).toLowerCase();
                        return nameA.localeCompare(nameB);
                      });
                      return (
                        <Paper 
                          key={examLog.examId || index} 
                          elevation={2} 
                          sx={{ p: 3, mb: 4, borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}
                        >
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, borderBottom: '1px solid #e0e0e0', pb: 1 }}>
                            <Typography variant="h6" sx={{ color: '#0277bd', fontWeight: 'bold' }}>
                              {examLog.examTitle}
                            </Typography>
                            {user && user.role === 'admin' && (
                              <Chip
                                label={`${examLog.logs.length} logs`}
                                size="small"
                                color="primary"
                                variant="outlined"
                              />
                            )}
                          </Box>
                          {/* Render an accordion for each student */}
                          <Box>
                            {studentIds.map((studentId) => {
                              const student = students.find(s => s._id === studentId) || {};
                              const logs = studentLogsMap[studentId];
                              return (
                                <Accordion key={studentId} sx={{ mb: 2 }}>
                                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <PersonIcon sx={{ color: '#1976d2' }} />
                                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>{student.name || studentId}</Typography>
                                      <Chip label={`${logs.length} logs`} size="small" color="primary" variant="outlined" sx={{ ml: 1 }} />
                                    </Box>
                                  </AccordionSummary>
                                  <AccordionDetails>
                                    <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 1 }}>
                                      <Table size="small">
                                        <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
                                          <TableRow>
                                            <TableCell width="20%" sx={{ fontWeight: 'bold' }}>Timestamp</TableCell>
                                            <TableCell width="20%" sx={{ fontWeight: 'bold' }}>Event Type</TableCell>
                                            <TableCell width="40%" sx={{ fontWeight: 'bold' }}>Description</TableCell>
                                            <TableCell width="20%" align="center" sx={{ fontWeight: 'bold' }}>Severity</TableCell>
                                          </TableRow>
                                        </TableHead>
                                        <TableBody>
                                          {logs.map((log, logIndex) => (
                                            <TableRow key={log._id || logIndex} hover>
                                              <TableCell>
                                                {(() => {
                                                  try {
                                                    const date = new Date(log.timestamp);
                                                    if (isNaN(date.getTime())) return 'Invalid Date';
                                                    return date.toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' });
                                                  } catch (e) {
                                                    return 'Invalid Date';
                                                  }
                                                })()}
                                              </TableCell>
                                              <TableCell>
                                                <Chip 
                                                  label={log.event_type} 
                                                  size="small" 
                                                  color={log.event_type === 'ALERT' ? 'error' : 'primary'} 
                                                  sx={{ minWidth: '70px', justifyContent: 'center' }}
                                                />
                                              </TableCell>
                                              <TableCell sx={{ wordBreak: 'break-word' }}>
                                                <Box 
                                                  component="span" 
                                                  sx={{ 
                                                    cursor: 'pointer', 
                                                    color: '#1976d2',
                                                    fontWeight: 'medium',
                                                    '&:hover': { textDecoration: 'underline' } 
                                                  }}
                                                  onClick={() => handleOpenWarningDialog({
                                                    description: log.description,
                                                    timestamp: log.timestamp,
                                                    event_type: log.event_type,
                                                    severity: log.severity,
                                                    student: student.name || log.student_name || log.user_id || '-',
                                                    exam: examLog.examTitle,
                                                    title: getWarningTitle(log.event_type, log.description)
                                                  })}
                                                >
                                                  {getWarningTitle(log.event_type, log.description)}
                                                </Box>
                                              </TableCell>
                                              <TableCell align="center">
                                                <Chip 
                                                  label={log.severity} 
                                                  size="small" 
                                                  color={
                                                    log.severity === 'CRITICAL' ? 'error' : 
                                                    log.severity === 'ALERT' ? 'warning' : 
                                                    log.severity === 'INFO' ? 'success' : 'default'
                                                  } 
                                                  sx={{ minWidth: '60px', justifyContent: 'center' }}
                                                />
                                              </TableCell>
                                            </TableRow>
                                          ))}
                                        </TableBody>
                                      </Table>
                                    </TableContainer>
                                  </AccordionDetails>
                                </Accordion>
                              );
                            })}
                          </Box>
                        </Paper>
                      );
                    });
                  })()}
                </Box>
              ) : (
                <Typography variant="body1" sx={{ mt: 2 }}>
                  No proctoring logs found. Logs will appear here when students take exams with proctoring enabled.
                </Typography>
              )}
            </Paper>
          )}
        </Box>
      </Box>
      
      {/* Add Student Dialog */}
      <Dialog 
        open={dialogOpen} 
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Add New Student
          <IconButton
            aria-label="close"
            onClick={handleCloseDialog}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box component="form" sx={{ mt: 1 }}>
            <TextField
              margin="dense"
              required
              fullWidth
              id="username"
              label="Username"
              name="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
            />
            
            <TextField
              margin="dense"
              required
              fullWidth
              name="password"
              label="Password"
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              helperText="Must be at least 8 characters with uppercase, lowercase, digit, and special character (@$!%*?&)"
            />
            
            <TextField
              margin="dense"
              fullWidth
              name="name"
              label="Full Name"
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            
            <TextField
              margin="dense"
              fullWidth
              name="email"
              label="Email Address"
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              helperText="Enter a valid email address (e.g., user@example.com)"
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseDialog} color="inherit">
            CANCEL
          </Button>
          <Button 
            onClick={handleCreateStudent} 
            variant="contained" 
            color="primary"
            disabled={loading}
          >
            {loading ? 'CREATING...' : 'CREATE'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Create Exam Dialog */}
      <Dialog 
        open={examDialogOpen} 
        onClose={handleCloseExamDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Create New Exam
          <IconButton
            aria-label="close"
            onClick={handleCloseExamDialog}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box component="form" sx={{ mt: 1 }}>
            <TextField
              margin="dense"
              required
              fullWidth
              id="examTitle"
              label="Exam Title"
              name="examTitle"
              value={examTitle}
              onChange={(e) => setExamTitle(e.target.value)}
            />
            
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  id="examDate"
                  label="Exam Date"
                  type="datetime-local"
                  name="examDate"
                  value={examDate}
                  onChange={(e) => setExamDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  id="numQuestions"
                  label="Number of Questions"
                  name="numQuestions"
                  type="number"
                  value={numQuestions}
                  onChange={(e) => {
                    // Allow any number or empty string while typing
                    const val = e.target.value;
                    if (val === '' || (/^\d+$/.test(val) && val.length <= 3)) {
                      setNumQuestions(val);
                    }
                  }}
                  onBlur={() => {
                    let val = parseInt(numQuestions, 10);
                    if (isNaN(val) || val < 10) val = 10;
                    if (val > 100) val = 100;
                    setNumQuestions(val);
                  }}
                  inputProps={{ min: 10, max: 100 }}
                  helperText="Min 10, Max 100"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  id="examDuration"
                  label="Duration (minutes)"
                  name="examDuration"
                  type="number"
                  value={examDuration}
                  onChange={(e) => {
                    let val = e.target.value;
                    if (val === '' || (/^\d+$/.test(val) && val.length <= 3)) {
                      setExamDuration(val);
                    }
                  }}
                  onBlur={() => {
                    let val = parseInt(examDuration, 10);
                    if (isNaN(val) || val < 10) val = 10;
                    if (val > 240) val = 240;
                    setExamDuration(val);
                  }}
                  inputProps={{ min: 10, max: 240 }}
                  helperText="You can adjust duration if needed (min 10, max 240)"
                />
              </Grid>
            </Grid>
            
            <TextField
              margin="dense"
              fullWidth
              id="examDescription"
              label="Exam Description"
              name="examDescription"
              value={examDescription}
              onChange={(e) => setExamDescription(e.target.value)}
              multiline
              rows={3}
              sx={{ mt: 2 }}
            />

            <TextField
              select
              margin="dense"
              fullWidth
              id="examSubject"
              label="Subject"
              name="examSubject"
              value={examSubject}
              onChange={(e) => setExamSubject(e.target.value)}
              sx={{ mt: 2 }}
              helperText="Select the subject for this exam. Questions will be pulled from the corresponding collection."
            >
              {Object.entries(availableSubjects).map(([key, subject]) => (
                <MenuItem key={key} value={key}>
                  {subject.name}
                </MenuItem>
              ))}
            </TextField>

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 3, mb: 1 }}>
              <Typography variant="subtitle1">
                Assign Students
              </Typography>
              {students.length > 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Checkbox
                    indeterminate={selectedStudents.length > 0 && selectedStudents.length < students.length}
                    checked={selectedStudents.length === students.length}
                    onChange={e => {
                      if (e.target.checked) {
                        setSelectedStudents(students.map(s => s.username));
                      } else {
                        setSelectedStudents([]);
                      }
                    }}
                  />
                  <Typography variant="body2" component="span" sx={{ ml: 1 }}>
                    Select All
                  </Typography>
                </Box>
              )}
            </Box>
            {students.length > 0 ? (
              <Paper variant="outlined" sx={{ maxHeight: 200, overflow: 'auto', p: 1 }}>
                <List dense>
                  {students.map((student) => (
                    <ListItem key={student._id} disablePadding>
                      <ListItemButton 
                        dense
                        onClick={() => {
                          if (selectedStudents.includes(student.username)) {
                            setSelectedStudents(selectedStudents.filter(username => username !== student.username));
                          } else {
                            setSelectedStudents([...selectedStudents, student.username]);
                          }
                        }}
                      >
                        <ListItemIcon>
                          <Checkbox
                            edge="start"
                            checked={selectedStudents.includes(student.username)}
                            tabIndex={-1}
                            disableRipple
                          />
                        </ListItemIcon>
                        <ListItemText 
                          primary={student.name || student.username} 
                          secondary={student.email || 'No email provided'} 
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              </Paper>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No students available. Please add students first.
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseExamDialog} color="inherit">
            CANCEL
          </Button>
          <Button 
            onClick={handleCreateExam} 
            variant="contained" 
            color="primary"
            disabled={loading || students.length === 0}
          >
            {loading ? 'CREATING...' : 'CREATE EXAM'}
          </Button>
        </DialogActions>
      </Dialog>
      
      <Snackbar 
        open={snackbarOpen} 
        autoHideDuration={6000} 
        onClose={() => setSnackbarOpen(false)}
      >
        <Alert 
          onClose={() => setSnackbarOpen(false)} 
          severity={snackbarSeverity} 
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {/* Warning Message Dialog - Styled like the student's warning popup */}
      <Dialog
        open={warningDialogOpen}
        onClose={() => setWarningDialogOpen(false)}
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: 2,
            overflow: 'hidden'
          }
        }}
      >
        {selectedWarning && (
          <>
            <Box sx={{ 
              bgcolor: '#d32f2f', 
              color: 'white', 
              p: 2, 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center'
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <WarningIcon sx={{ mr: 1 }} />
                <Typography variant="h6" component="div">
                  {selectedWarning.title || 'Warning Alert'}
                </Typography>
              </Box>
              <IconButton 
                onClick={() => setWarningDialogOpen(false)} 
                sx={{ color: 'white' }}
                size="small"
              >
                <CloseIcon />
              </IconButton>
            </Box>
            
            <DialogContent sx={{ pt: 3, pb: 2 }}>
              <Box sx={{ mb: 3 }}>
                <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', fontWeight: 'medium' }}>
                  {selectedWarning.description}
                </Typography>
                
                {/* Warning count indicator - similar to what students see */}
                <Box sx={{ mt: 3, mb: 1 }}>
                  <Typography variant="subtitle2" gutterBottom color="error">
                    This is how the warning appeared to the student
                  </Typography>
                  <LinearProgress 
                    variant="determinate" 
                    value={30} 
                    sx={{ 
                      height: 8, 
                      borderRadius: 4,
                      bgcolor: '#ffcdd2',
                      '& .MuiLinearProgress-bar': {
                        bgcolor: '#d32f2f'
                      }
                    }} 
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                    Warning 3 of 10 - Student was warned that continued violations may result in exam termination
                  </Typography>
                </Box>
              </Box>
              
              <Divider sx={{ my: 2 }} />
              
              {/* Context information */}
              <Typography variant="subtitle2" gutterBottom sx={{ color: '#555', fontWeight: 'bold', mt: 2 }}>
                Event Details:
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Exam</Typography>
                  <Typography variant="body2">{selectedWarning.exam}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Student</Typography>
                  <Typography variant="body2">{selectedWarning.student}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Timestamp</Typography>
                  <Typography variant="body2">
                    {new Date(selectedWarning.timestamp).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Event Type</Typography>
                  <Chip 
                    label={selectedWarning.event_type} 
                    size="small" 
                    color={selectedWarning.event_type === 'ALERT' ? 'error' : 'primary'}
                  />
                </Grid>
              </Grid>
            </DialogContent>
            
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button 
                onClick={() => setWarningDialogOpen(false)} 
                variant="contained" 
                color="primary"
                size="small"
              >
                Close
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Student Answers Dialog */}
      <Dialog
        open={answersDialogOpen}
        onClose={() => setAnswersDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle sx={{ bgcolor: '#f5f5f5', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <AssessmentIcon color="primary" sx={{ mr: 1 }} />
            Student Answers
          </Box>
          <IconButton onClick={() => setAnswersDialogOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {selectedResult && (
            <Box>
              {/* Get questions from either 'questions' or 'questions_with_answers' field */}
              {(() => {
                const questions = selectedResult.questions || selectedResult.questions_with_answers || [];

                return (
                  <>


                    {/* Summary Statistics */}
                    {questions && questions.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', mr: 2 }}>
                      📋 Exam Results
                    </Typography>
                    <Chip
                      label={`${selectedResult.score !== undefined ? Math.round(selectedResult.score) : 0}%`}
                      sx={{
                        bgcolor: '#f44336',
                        color: 'white',
                        fontWeight: 'bold',
                        fontSize: '0.875rem'
                      }}
                    />
                  </Box>

                  <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={12} sm={3}>
                      <Paper sx={{ p: 2, textAlign: 'center', border: '1px solid #e0e0e0' }}>
                        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                          Total Questions
                        </Typography>
                        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#333' }}>
                          {questions.length}
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <Paper sx={{ p: 2, textAlign: 'center', border: '1px solid #e0e0e0' }}>
                        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                          Correct Answers
                        </Typography>
                        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#4caf50' }}>
                          {questions.filter((question, index) => {
                            const studentAnswer = selectedResult.answers?.[question.question_id] || question.student_answer || '';
                            const correctAnswer = question.correctAnswer || question.correct_answer || '';
                            // Only count as correct if student provided an answer AND it matches the correct answer
                            return studentAnswer.trim() !== '' && studentAnswer.trim().toLowerCase() === correctAnswer.trim().toLowerCase();
                          }).length}
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <Paper sx={{ p: 2, textAlign: 'center', border: '1px solid #e0e0e0' }}>
                        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                          Incorrect Answers
                        </Typography>
                        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#f44336' }}>
                          {questions.filter((question, index) => {
                            const studentAnswer = selectedResult.answers?.[question.question_id] || question.student_answer || '';
                            const correctAnswer = question.correctAnswer || question.correct_answer || '';
                            return studentAnswer.trim() !== '' && studentAnswer.trim().toLowerCase() !== correctAnswer.trim().toLowerCase();
                          }).length}
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <Paper sx={{ p: 2, textAlign: 'center', border: '1px solid #e0e0e0' }}>
                        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                          Not Answered
                        </Typography>
                        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#ff9800' }}>
                          {questions.filter((question, index) => {
                            const studentAnswer = selectedResult.answers?.[question.question_id] || question.student_answer || '';
                            return studentAnswer.trim() === '';
                          }).length}
                        </Typography>
                      </Paper>
                    </Grid>
                  </Grid>
                </Box>
              )}

              <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 2, fontWeight: 'bold' }}>
                Detailed Results
              </Typography>
              
              {questions && questions.length > 0 ? (
                questions.map((question, index) => {
                  // Get the student's answer for this question
                  const studentAnswer = selectedResult.answers?.[question.question_id] || question.student_answer || '';
                  const correctAnswer = question.correctAnswer || question.correct_answer || '';

                  // Check if the answer is correct (student must provide an answer AND it must match)
                  const isCorrect = studentAnswer.trim() !== '' && studentAnswer.trim().toLowerCase() === correctAnswer.trim().toLowerCase();

                  return (
                    <QuestionAccordion
                      key={question.question_id || question._id || index}
                      question={question}
                      index={index}
                      studentAnswer={studentAnswer}
                      correctAnswer={correctAnswer}
                      isCorrect={isCorrect}
                    />
                  );
                })
              ) : (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                    No questions available for this submission.
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    This might be an older submission created before question details were stored with results.
                  </Typography>
                  {selectedResult.answers && (
                    <Box sx={{ mt: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>Student Answers:</Typography>
                      <Typography variant="body2" component="pre" sx={{ textAlign: 'left', fontSize: '0.875rem' }}>
                        {JSON.stringify(selectedResult.answers, null, 2)}
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}
                  </>
                );
              })()}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAnswersDialogOpen(false)} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Proctoring Logs Dialog */}
      <Dialog
        open={viewLogsDialogOpen}
        onClose={() => setViewLogsDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          Proctoring Logs
          <IconButton
            aria-label="close"
            onClick={() => setViewLogsDialogOpen(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {proctoringLogs.length > 0 ? (
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Timestamp</TableCell>
                    <TableCell>Event Type</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>Severity</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {proctoringLogs
                    .filter(log => 
                      (!selectedExamId || log.exam_id === selectedExamId) && 
                      (!selectedStudentId || log.user_id === selectedStudentId)
                    )
                    .map((log, index) => {
                      // Handle timestamp formatting safely
                      let formattedTime = 'Unknown';
                      try {
                        if (log.timestamp) {
                          formattedTime = new Date(log.timestamp).toLocaleString();
                        }
                      } catch (e) {
                        console.error('Error formatting timestamp:', e);
                      }
                      
                      return (
                        <TableRow key={log._id || index}>
                          <TableCell>{formattedTime}</TableCell>
                          <TableCell>
                            <Chip 
                              label={log.event_type || 'Unknown'} 
                              size="small" 
                              color={
                                log.event_type === 'PERSON' ? 'error' :
                                log.event_type === 'GAZE' ? 'warning' :
                                log.event_type === 'PHONE' ? 'error' :
                                log.event_type === 'AUDIO' ? 'warning' :
                                log.event_type === 'MOUTH' ? 'warning' :
                                log.event_type === 'HEAD' ? 'warning' :
                                log.event_type === 'SPOOF' ? 'error' :
                                log.event_type === 'SESSION' ? 'info' :
                                log.event_type === 'FOCUS' ? 'warning' : 'default'
                              } 
                            />
                          </TableCell>
                          <TableCell>{log.description || 'No description'}</TableCell>
                          <TableCell>
                            <Chip 
                              label={log.severity || 'Unknown'} 
                              size="small" 
                              color={
                                log.severity === 'CRITICAL' ? 'error' : 
                                log.severity === 'ALERT' ? 'error' : 
                                log.severity === 'WARNING' ? 'warning' :
                                log.severity === 'INFO' ? 'success' : 'default'
                              } 
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })
                  }
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography variant="body1" sx={{ mt: 2 }}>
              No proctoring logs found for this exam or student.
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setViewLogsDialogOpen(false)} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default TeacherDashboard;
